import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ReactNode } from "react";
import { cn } from "../cn";
import { isSecondPressOfOpening } from "./dialog-dismiss";

// A panel from one edge of the screen (ADR-050, point 3): the navigation below `lg` from the left, the detail
// panel below `lg` from the right. A modal dialog of Base UI: focus stays inside, Esc and the backdrop close it, but
// not the second press of the double click on a row that opened it, as for `Dialog`.

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: "left" | "right";
  /** Accessible name of the dialog; the content shows its own heading. */
  title: ReactNode;
  children: ReactNode;
  className?: string;
  /** The element that takes the focus when it opens, instead of the first one; null for the first one. */
  initialFocus?: (() => HTMLElement | null) | undefined;
}

export function Sheet({ open, onOpenChange, side, title, children, className, initialFocus }: SheetProps) {
  return (
    <BaseDialog.Root
      open={open}
      onOpenChange={(next, details) => {
        if (!next && isSecondPressOfOpening(details)) return details.cancel();
        onOpenChange(next);
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 z-20 bg-black/20 transition-opacity duration-150 ease-out data-[ending-style]:opacity-0 data-[starting-style]:opacity-0 dark:bg-black/50" />
        <BaseDialog.Popup
          initialFocus={initialFocus ? () => initialFocus() ?? true : true}
          className={cn(
            "fixed inset-y-0 z-20 flex max-w-full flex-col bg-background shadow-dialog transition-transform duration-150 ease-out",
            side === "left"
              ? "left-0 border-r border-border data-[ending-style]:-translate-x-full data-[starting-style]:-translate-x-full"
              : "right-0 border-l border-border data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full",
            className,
          )}
        >
          <BaseDialog.Title className="sr-only">{title}</BaseDialog.Title>
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
